import { useEffect, useMemo, useRef, useState } from "react";
import { checkInEmployeeAttendance, checkOutEmployeeAttendance } from "../api/instateamAttendanceApi";
import {
  createEmployeeQuickNote,
  deleteEmployeeQuickNote,
  getEmployeeDashboard,
} from "../api/instateamDashboardApi";
import { createEmployeeTask, updateEmployeeTaskStatus } from "../api/instateamTasksApi";
import {
  AppIcon,
  EmployeeAvatar,
  StatusBanner,
} from "../components/InstateamEmployeeComponents";

import LiveClockCard from "../components/LiveClockCard";

const DASHBOARD_REFRESH_MS = 60000;
const QUICK_NOTE_TONES = ["amber", "green", "blue", "violet", "rose", "sky"];

const todayDateKey = () => new Date().toISOString().slice(0, 10);

const formatDeadlineLabel = (dateKey) => {
  const date = dateKey ? new Date(`${dateKey}T00:00:00`) : null;
  if (!date || Number.isNaN(date.getTime())) return "No deadline";
  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
};

const parseTaskDeadline = (task) => {
  const timeMatch = String(task?.details || "").match(/Deadline time:\s*(\d{2}:\d{2})/i);
  const time = timeMatch?.[1] || "23:59";
  const dateTime = task?.dueDate ? new Date(`${task.dueDate}T${time}:00`) : null;
  return dateTime && !Number.isNaN(dateTime.getTime()) ? dateTime : null;
};

const isTaskTimeOverdue = (task) => {
  if (!task || task.status === "completed") return false;
  const deadline = parseTaskDeadline(task);
  return deadline ? deadline.getTime() < Date.now() : task.status === "overdue";
};

const getTaskDeadlineTime = (task) => {
  const timeMatch = String(task?.details || "").match(/Deadline time:\s*(\d{2}:\d{2})/i);
  return timeMatch?.[1] || "";
};

const TONE_HEX_MAP = {
  blue: "#2563eb",
  green: "#22c55e",
  amber: "#f59e0b",
  violet: "#8b5cf6",
  red: "#ef4444",
  sky: "#06b6d4",
  slate: "#94a3b8",
};

const PRIORITY_META = {
  urgent: { label: "Urgent", className: "danger" },
  high: { label: "High", className: "danger" },
  medium: { label: "Medium", className: "warning" },
  low: { label: "Low", className: "success" },
};

const ATTENDANCE_META = {
  present: { label: "Present", className: "present", icon: "checkCircle" },
  late: { label: "Late", className: "late", icon: "clock" },
  absent: { label: "Absent", className: "absent", icon: "calendarX" },
  on_leave: { label: "On Leave", className: "leave", icon: "plane" },
  weekly_off: { label: "Weekly Off", className: "off", icon: "calendar" },
  holiday: { label: "Holiday", className: "holiday", icon: "calendar" },
};

const normalizeText = (value) => String(value || "").trim().toLowerCase();

const matchesSearch = (searchText, values) => {
  const safeSearch = normalizeText(searchText);
  if (!safeSearch) return true;

  return values.some((value) => normalizeText(value).includes(safeSearch));
};

const formatNumberValue = (value) => {
  const amount = Number(value || 0);
  if (Number.isInteger(amount)) {
    return String(amount);
  }

  return amount.toFixed(1).replace(/\.0$/, "");
};

const parseTimeToMinutes = (timeValue) => {
  const safeValue = String(timeValue || "").trim();
  if (!/^\d{2}:\d{2}$/.test(safeValue)) {
    return 0;
  }

  const [hours, minutes] = safeValue.split(":").map(Number);
  return hours * 60 + minutes;
};

const formatTimelineLabel = (minutesValue) => {
  const hours = Math.floor(minutesValue / 60);
  const minutes = minutesValue % 60;
  const period = hours >= 12 ? "PM" : "AM";
  const normalizedHours = hours % 12 || 12;
  return minutes ? `${normalizedHours}:${String(minutes).padStart(2, "0")} ${period}` : `${normalizedHours} ${period}`;
};

const buildScheduleMarkers = (shiftStart, shiftEnd) => {
  const startMinutes = parseTimeToMinutes(shiftStart);
  const endMinutes = parseTimeToMinutes(shiftEnd);
  const totalMinutes = Math.max(60, endMinutes - startMinutes);
  const stepMinutes =
    totalMinutes <= 360 ? 60 : totalMinutes <= 600 ? 90 : 120;

  const markers = [];
  for (let minute = startMinutes; minute <= endMinutes; minute += stepMinutes) {
    markers.push({
      value: minute,
      label: formatTimelineLabel(minute),
    });
  }

  if (!markers.length || markers[markers.length - 1]?.value !== endMinutes) {
    markers.push({
      value: endMinutes,
      label: formatTimelineLabel(endMinutes),
    });
  }

  return markers;
};

const buildConicGradient = (segments = []) => {
  const safeSegments = Array.isArray(segments) ? segments : [];
  if (!safeSegments.length) {
    return "conic-gradient(#e5e7eb 0 100%)";
  }

  let start = 0;
  const parts = safeSegments
    .filter((segment) => Number(segment.percentage || 0) > 0)
    .map((segment) => {
      const segmentStart = start;
      start += Number(segment.percentage || 0);
      return `${TONE_HEX_MAP[segment.tone] || TONE_HEX_MAP.blue} ${segmentStart}% ${Math.min(
        start,
        100
      )}%`;
    });

  if (start < 100) {
    parts.push(`#e5e7eb ${start}% 100%`);
  }

  return `conic-gradient(${parts.join(", ")})`;
};

const buildTrendCoordinates = (points = []) => {
  const chartWidth = 380;
  const chartHeight = 176;
  const paddingX = 36;
  const paddingTop = 20;
  const paddingBottom = 34;
  const innerWidth = chartWidth - paddingX * 2;
  const innerHeight = chartHeight - paddingTop - paddingBottom;
  const safePoints = Array.isArray(points) && points.length ? points : [{ shortLabel: "Today", value: 0 }];
  const maxValue = Math.max(1, ...safePoints.map((point) => Number(point.value || 0)));
  const stepX = safePoints.length > 1 ? innerWidth / (safePoints.length - 1) : 0;

  const linePoints = safePoints.map((point, index) => {
    const x = paddingX + stepX * index;
    const y = paddingTop + innerHeight - (Number(point.value || 0) / maxValue) * innerHeight;
    return { ...point, x, y };
  });

  const ticks = Array.from({ length: 4 }, (_, index) => {
    const value = Math.round((maxValue / 3) * (3 - index));
    const y = paddingTop + (innerHeight / 3) * index;
    return { value, y };
  });

  return {
    chartWidth,
    chartHeight,
    paddingX,
    paddingTop,
    paddingBottom,
    linePoints,
    ticks,
  };
};

const buildFilteredDashboard = (dashboard, searchText) => {
  if (!dashboard) return null;
  if (!normalizeText(searchText)) return dashboard;

  return {
    ...dashboard,
    todoList: (dashboard.todoList || []).filter((task) =>
      matchesSearch(searchText, [task.title, task.details, task.priority, task.dueLabel, task.status])
    ),
    activityFeed: (dashboard.activityFeed || []).filter((item) =>
      matchesSearch(searchText, [item.title, item.subtitle, item.tone])
    ),
    upcomingEvents: (dashboard.upcomingEvents || []).filter((item) =>
      matchesSearch(searchText, [item.title, item.subtitle, item.meta])
    ),
    quickNotes: (dashboard.quickNotes || []).filter((item) =>
      matchesSearch(searchText, [item.title, item.description, item.timeLabel])
    ),
    schedule: {
      ...(dashboard.schedule || {}),
      items: (dashboard.schedule?.items || []).filter((item) =>
        matchesSearch(searchText, [item.title, item.startLabel, item.endLabel])
      ),
    },
  };
};

function DashboardMetricCard({
  icon,
  tone,
  label,
  value,
  helper,
  helperTone = "",
}) {
  return (
    <article className="employee-dashboard-metric-card">
      <div className={`employee-dashboard-metric-icon ${tone}`}>
        <AppIcon name={icon} />
      </div>
      <div className="employee-dashboard-metric-copy">
        <span>{label}</span>
        <strong>{value}</strong>
        <small className={helperTone}>{helper}</small>
      </div>
    </article>
  );
}

function DashboardCardHeader({ title, actionLabel = "", onAction = null }) {
  return (
    <div className="employee-dashboard-card-header">
      <h3>{title}</h3>
      {actionLabel && onAction ? (
        <button type="button" className="employee-dashboard-card-link" onClick={onAction}>
          {actionLabel}
        </button>
      ) : null}
    </div>
  );
}

function EmptyState({ icon, text }) {
  return (
    <div className="employee-dashboard-empty-state">
      <div className="employee-dashboard-empty-icon">
        <AppIcon name={icon} />
      </div>
      <p>{text}</p>
    </div>
  );
}

function TaskOverviewChart({ overview }) {
  const chart = buildTrendCoordinates(overview?.trend || []);

  return (
    <div className="employee-dashboard-overview-body">
      <div className="employee-dashboard-donut-wrap">
        <div
          className="employee-dashboard-donut"
          style={{ backgroundImage: buildConicGradient(overview?.segments || []) }}
        >
          <div className="employee-dashboard-donut-center">
            <strong>{formatNumberValue(overview?.totalTasks || 0)}</strong>
            <span>Total Tasks</span>
          </div>
        </div>

        <div className="employee-dashboard-donut-legend">
          {(overview?.segments || []).map((segment) => (
            <div key={segment.key} className="employee-dashboard-donut-legend-item">
              <span
                className={`employee-dashboard-tone-dot ${segment.tone}`}
                aria-hidden="true"
              />
              <div>
                <strong>{segment.label}</strong>
                <small>
                  {formatNumberValue(segment.count)} ({segment.percentage}%)
                </small>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="employee-dashboard-line-chart-card">
        <svg
          className="employee-dashboard-line-chart"
          viewBox={`0 0 ${chart.chartWidth} ${chart.chartHeight}`}
          role="img"
          aria-label="Task completion trend"
        >
          {chart.ticks.map((tick) => (
            <g key={tick.y}>
              <line
                x1={chart.paddingX}
                y1={tick.y}
                x2={chart.chartWidth - chart.paddingX}
                y2={tick.y}
                className="employee-dashboard-grid-line"
              />
              <text x="10" y={tick.y + 4} className="employee-dashboard-axis-label">
                {tick.value}
              </text>
            </g>
          ))}

          {chart.linePoints.length > 1 ? (
            <polyline
              fill="none"
              stroke={TONE_HEX_MAP.blue}
              strokeWidth="3"
              strokeLinejoin="round"
              strokeLinecap="round"
              points={chart.linePoints.map((point) => `${point.x},${point.y}`).join(" ")}
            />
          ) : null}

          {chart.linePoints.map((point) => (
            <g key={point.dateKey || point.shortLabel}>
              <circle
                cx={point.x}
                cy={point.y}
                r="5.5"
                fill="#ffffff"
                stroke={TONE_HEX_MAP.blue}
                strokeWidth="3"
              />
              <text x={point.x} y={point.y - 12} textAnchor="middle" className="employee-dashboard-point-label">
                {point.value}
              </text>
              <text
                x={point.x}
                y={chart.chartHeight - 6}
                textAnchor="middle"
                className="employee-dashboard-axis-label"
              >
                {point.shortLabel}
              </text>
            </g>
          ))}
        </svg>
      </div>
    </div>
  );
}

function TimeBreakdownChart({ breakdown }) {
  const segments = (breakdown?.segments || []).map((segment) => ({
    ...segment,
    percentage:
      breakdown?.loggedMinutes > 0
        ? Math.round((Number(segment.minutes || 0) / Number(breakdown.loggedMinutes || 1)) * 100)
        : 0,
  }));

  return (
    <div className="employee-dashboard-time-body">
      <div
        className="employee-dashboard-donut time"
        style={{ backgroundImage: buildConicGradient(segments) }}
      >
        <div className="employee-dashboard-donut-center">
          <strong>{breakdown?.loggedLabel || "0h 00m"}</strong>
          <span>of {breakdown?.targetLabel || "0h 00m"}</span>
        </div>
      </div>

      <div className="employee-dashboard-time-legend">
        {(breakdown?.segments || []).map((segment) => (
          <div key={segment.key} className="employee-dashboard-time-legend-item">
            <span className={`employee-dashboard-tone-dot ${segment.tone}`} />
            <div>
              <strong>{segment.label}</strong>
              <small>{segment.valueLabel}</small>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ScheduleTimeline({ schedule, onViewCalendar }) {
  const markers = buildScheduleMarkers(schedule?.shiftStart, schedule?.shiftEnd);

  return (
    <article className="employee-dashboard-card employee-dashboard-schedule-card">
      <DashboardCardHeader title="Today's Schedule" actionLabel="View Calendar" onAction={onViewCalendar} />

      <div className="employee-dashboard-schedule-date">{schedule?.dateLabel || "Today"}</div>

      {schedule?.items?.length ? (
        <div className="employee-dashboard-schedule-wrap">
          <div className="employee-dashboard-schedule-track">
            {schedule.items.map((item) => (
              <article
                key={item._id}
                className={`employee-dashboard-schedule-item ${item.tone || "blue"}`}
                style={{
                  left: `${Math.max(0, item.leftPercent || 0)}%`,
                  width: `${Math.max(12, item.widthPercent || 0)}%`,
                }}
              >
                <strong>{item.title}</strong>
                <span>
                  {item.startLabel} - {item.endLabel}
                </span>
              </article>
            ))}
          </div>

          <div className="employee-dashboard-schedule-markers">
            {markers.map((marker) => (
              <span key={`${marker.value}-${marker.label}`}>{marker.label}</span>
            ))}
          </div>
        </div>
      ) : (
        <EmptyState icon="calendar" text="No scheduled tasks are lined up for today." />
      )}
    </article>
  );
}

export default function EmployeeDashboardPage({
  employee,
  token,
  searchText = "",
  onNavigate = () => {},
}) {
  const [dashboard, setDashboard] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);
  const [isAttendanceSaving, setIsAttendanceSaving] = useState(false);
  const [isAddMenuOpen, setIsAddMenuOpen] = useState(false);
  const [isTodoModalOpen, setIsTodoModalOpen] = useState(false);
  const [todoForm, setTodoForm] = useState({
    title: "",
    dueDate: todayDateKey(),
    dueTime: "",
  });
  const [isSavingTodo, setIsSavingTodo] = useState(false);
  const [updatingTodoId, setUpdatingTodoId] = useState("");
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [noteForm, setNoteForm] = useState({ title: "", details: "" });
  const [isSavingNote, setIsSavingNote] = useState(false);
  const [deletingNoteId, setDeletingNoteId] = useState("");
  const addMenuRef = useRef(null);

  async function fetchDashboard({ silent = false } = {}) {
    if (!token) return;

    try {
      if (silent) {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }

      const payload = await getEmployeeDashboard(token);
      setDashboard(payload.dashboard || null);
    } catch (error) {
      setStatusMessage({
        type: "error",
        text: error.message || "Unable to load dashboard data right now.",
      });
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }

  useEffect(() => {
    fetchDashboard();
    const intervalId = window.setInterval(() => {
      fetchDashboard({ silent: true });
    }, DASHBOARD_REFRESH_MS);

    return () => window.clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    const handleDocumentClick = (event) => {
      if (!addMenuRef.current?.contains(event.target)) {
        setIsAddMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleDocumentClick);
    return () => document.removeEventListener("mousedown", handleDocumentClick);
  }, []);

  const filteredDashboard = useMemo(
    () => buildFilteredDashboard(dashboard, searchText),
    [dashboard, searchText]
  );

  const addActions = useMemo(() => {
    const actions = [
      { key: "leaves", label: "Apply Leave", icon: "plane" },
      { key: "chat", label: "Open Chat", icon: "messageSquare" },
      { key: "complaints", label: "Raise Complaint", icon: "alertCircle" },
      { key: "documents", label: "View Documents", icon: "fileText" },
    ];

    if (filteredDashboard?.actions?.canAssignTasks) {
      actions.unshift({ key: "tasks", label: "Assign Task", icon: "plus" });
    }

    return actions;
  }, [filteredDashboard?.actions?.canAssignTasks]);

  const attendanceMeta =
    ATTENDANCE_META[String(filteredDashboard?.checkInCard?.status || "").toLowerCase()] ||
    ATTENDANCE_META.present;

  const attendanceHeadline = filteredDashboard?.checkInCard?.checkInLabel
    ? filteredDashboard.checkInCard.checkInLabel
    : filteredDashboard?.checkInCard?.label || "Not Marked";
  const attendanceSubline = filteredDashboard?.checkInCard?.canCheckOut
    ? `Working since ${filteredDashboard.checkInCard.workingSinceLabel}`
    : filteredDashboard?.checkInCard?.checkOutLabel
      ? `Checked out at ${filteredDashboard.checkInCard.checkOutLabel}`
      : filteredDashboard?.checkInCard?.canCheckIn
        ? "Ready to start your workday"
        : filteredDashboard?.checkInCard?.label || "Status unavailable";

  async function handleAttendanceAction() {
    if (!token || !filteredDashboard?.checkInCard) return;

    try {
      setIsAttendanceSaving(true);
      setStatusMessage(null);

      if (filteredDashboard.checkInCard.canCheckOut) {
        await checkOutEmployeeAttendance(token);
        setStatusMessage({ type: "success", text: "Checked out successfully." });
      } else if (filteredDashboard.checkInCard.canCheckIn) {
        await checkInEmployeeAttendance(token);
        setStatusMessage({ type: "success", text: "Checked in successfully." });
      }

      await fetchDashboard({ silent: true });
    } catch (error) {
      setStatusMessage({ type: "error", text: error.message });
    } finally {
      setIsAttendanceSaving(false);
    }
  }

  const handleNavigate = (view) => {
    setIsAddMenuOpen(false);
    onNavigate(view);
  };

  function openTodoModal() {
    setTodoForm({ title: "", dueDate: todayDateKey(), dueTime: "" });
    setIsTodoModalOpen(true);
  }

  function closeTodoModal() {
    if (isSavingTodo) return;
    setIsTodoModalOpen(false);
  }

  async function handleAddTodoTask(event) {
    event.preventDefault();
    const title = todoForm.title.trim();

    if (!title) {
      setStatusMessage({ type: "warning", text: "Please enter a task name." });
      return;
    }

    if (!todoForm.dueDate) {
      setStatusMessage({ type: "warning", text: "Please select a deadline." });
      return;
    }

    if (!todoForm.dueTime) {
      setStatusMessage({ type: "warning", text: "Please select a deadline time." });
      return;
    }

    try {
      setIsSavingTodo(true);
      setStatusMessage(null);
      const currentEmployeeId = employee?._id || employee?.id;
      await createEmployeeTask(token, {
        title,
        details: `${title}\nDeadline time: ${todoForm.dueTime}`,
        ...(currentEmployeeId ? { assignedTo: currentEmployeeId } : {}),
        project: "Personal To Do",
        dueDate: todoForm.dueDate,
        estimatedMinutes: 60,
        priority: "medium",
      });
      setStatusMessage({ type: "success", text: "Task added successfully." });
      setIsTodoModalOpen(false);
      setTodoForm({ title: "", dueDate: todayDateKey(), dueTime: "" });
      await fetchDashboard({ silent: true });
    } catch (error) {
      setStatusMessage({ type: "error", text: error.message });
    } finally {
      setIsSavingTodo(false);
    }
  }

  async function handleToggleTodoTask(task) {
    if (!task?._id || updatingTodoId || task.status === "completed") return;

    const nextStatus = "completed";

    try {
      setUpdatingTodoId(task._id);
      setStatusMessage(null);
      await updateEmployeeTaskStatus(token, task._id, nextStatus);
      await fetchDashboard({ silent: true });
    } catch (error) {
      setStatusMessage({ type: "error", text: error.message });
    } finally {
      setUpdatingTodoId("");
    }
  }

  function openNoteModal() {
    setNoteForm({ title: "", details: "" });
    setIsNoteModalOpen(true);
  }

  function closeNoteModal() {
    if (isSavingNote) return;
    setIsNoteModalOpen(false);
  }

  async function handleAddQuickNote(event) {
    event.preventDefault();
    const title = noteForm.title.trim();
    const details = noteForm.details.trim();

    if (!title || !details) {
      setStatusMessage({ type: "warning", text: "Note headline and details are required." });
      return;
    }

    try {
      setIsSavingNote(true);
      setStatusMessage(null);
      const nextTone = QUICK_NOTE_TONES[(filteredDashboard?.quickNotes?.length || 0) % QUICK_NOTE_TONES.length];
      await createEmployeeQuickNote(token, { title, details, tone: nextTone });
      setStatusMessage({ type: "success", text: "Note added successfully." });
      setIsNoteModalOpen(false);
      setNoteForm({ title: "", details: "" });
      await fetchDashboard({ silent: true });
    } catch (error) {
      setStatusMessage({ type: "error", text: error.message });
    } finally {
      setIsSavingNote(false);
    }
  }

  async function handleDeleteQuickNote(noteId) {
    if (!noteId || deletingNoteId) return;

    try {
      setDeletingNoteId(noteId);
      setStatusMessage(null);
      await deleteEmployeeQuickNote(token, noteId);
      await fetchDashboard({ silent: true });
    } catch (error) {
      setStatusMessage({ type: "error", text: error.message });
    } finally {
      setDeletingNoteId("");
    }
  }

  if (isLoading && !dashboard) {
    return (
      <section className="employee-dashboard-page">
        <div className="employee-dashboard-loading-card">
          <div className="employee-dashboard-empty-icon">
            <AppIcon name="layoutDashboard" />
          </div>
          <h2>Loading dashboard...</h2>
          <p>We are gathering your latest tasks, attendance, leaves, and updates.</p>
        </div>
      </section>
    );
  }

  if (!filteredDashboard) {
    return (
      <section className="employee-dashboard-page">
        <div className="employee-dashboard-loading-card">
          <div className="employee-dashboard-empty-icon">
            <AppIcon name="alertCircle" />
          </div>
          <h2>Dashboard unavailable</h2>
          <p>We could not load the employee dashboard right now.</p>
          <button type="button" className="primary-button" onClick={() => fetchDashboard()}>
            Retry
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="employee-dashboard-page">
      <div className="employee-dashboard-hero">
        <div className="employee-dashboard-hero-copy">
          <h1>
            {filteredDashboard.greeting?.salutation || "Welcome back"},{" "}
            {filteredDashboard.greeting?.firstName || "there"}
          </h1>
          <p>Here&apos;s what&apos;s happening with your work today.</p>
        </div>

        <div className="employee-dashboard-hero-actions">
          <div className="employee-dashboard-date-chip">
            <div className="employee-dashboard-date-icon">
              <AppIcon name="calendar" />
            </div>
            <div>
              <strong>{filteredDashboard.date?.dayLabel}</strong>
              <span>{filteredDashboard.date?.weekdayLabel}</span>
            </div>
          </div>


    <div style={{ padding: 20 }}>
      <LiveClockCard
        width="220px"
        height="80px"
      />
    </div>



          <div className="employee-dashboard-add-menu" ref={addMenuRef}>
            <button
              type="button"
              className="employee-dashboard-add-trigger"
              onClick={() => setIsAddMenuOpen((current) => !current)}
            >
              <AppIcon name="plus" />
              <span>Add</span>
              <AppIcon name="chevronDown" />
            </button>

            {isAddMenuOpen ? (
              <div className="employee-dashboard-add-dropdown">
                {addActions.map((action) => (
                  <button
                    key={action.key}
                    type="button"
                    onClick={() => handleNavigate(action.key)}
                  >
                    <AppIcon name={action.icon} />
                    <span>{action.label}</span>
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {statusMessage ? (
        <StatusBanner
          type={statusMessage.type}
          text={statusMessage.text}
          onClose={() => setStatusMessage(null)}
        />
      ) : null}

      {normalizeText(searchText) ? (
        <div className="employee-dashboard-filter-note">
          Showing filtered dashboard results for &quot;{searchText}&quot;
          {isRefreshing ? <span>Refreshing...</span> : null}
        </div>
      ) : null}

      <div className="employee-dashboard-metrics">
        <DashboardMetricCard
          icon="clipboard"
          tone="blue"
          label="Tasks Due Today"
          value={formatNumberValue(filteredDashboard.metrics?.tasksDueToday?.count)}
          helper={
            filteredDashboard.metrics?.tasksDueToday?.highPriorityCount
              ? `+ ${filteredDashboard.metrics.tasksDueToday.highPriorityCount} high priority`
              : "All on track"
          }
          helperTone={
            filteredDashboard.metrics?.tasksDueToday?.highPriorityCount ? "danger" : ""
          }
        />
        <DashboardMetricCard
          icon="checkCircle"
          tone="green"
          label="Tasks Completed"
          value={formatNumberValue(filteredDashboard.metrics?.tasksCompleted?.count)}
          helper={filteredDashboard.metrics?.tasksCompleted?.helperText || "No change"}
          helperTone={filteredDashboard.metrics?.tasksCompleted?.direction === "down" ? "danger" : "success"}
        />
        <DashboardMetricCard
          icon="clock"
          tone="violet"
          label="Time Logged"
          value={filteredDashboard.metrics?.timeLogged?.label || "0h 00m"}
          helper={filteredDashboard.metrics?.timeLogged?.helperText || "Today"}
          helperTone={filteredDashboard.metrics?.timeLogged?.direction === "down" ? "danger" : "success"}
        />
        <DashboardMetricCard
          icon="calendarCheck"
          tone="amber"
          label="Attendance"
          value={filteredDashboard.metrics?.attendance?.label || "Not Marked"}
          helper={
            filteredDashboard.metrics?.attendance?.checkInLabel
              ? `Checked in ${filteredDashboard.metrics.attendance.checkInLabel}`
              : "Not checked in yet"
          }
          helperTone=""
        />
        <DashboardMetricCard
          icon="plane"
          tone="red"
          label="Leaves Balance"
          value={`${formatNumberValue(filteredDashboard.metrics?.leavesBalance?.availableDays)} Days`}
          helper="Total available"
          helperTone=""
        />
        <DashboardMetricCard
          icon="target"
          tone="sky"
          label="Focus Time"
          value={filteredDashboard.metrics?.focusTime?.label || "0h 00m"}
          helper="Today"
          helperTone=""
        />
      </div>

      <div className="employee-dashboard-content-grid">
        <article className="employee-dashboard-card employee-dashboard-overview-card">
          <DashboardCardHeader
            title="My Task Overview"
            actionLabel="View All"
            onAction={() => handleNavigate("tasks")}
          />
          <TaskOverviewChart overview={filteredDashboard.taskOverview} />
        </article>

        <article className="employee-dashboard-card employee-dashboard-todo-card">
          <DashboardCardHeader
            title="To Do List"
            actionLabel="+ Add Task"
            onAction={openTodoModal}
          />

          {filteredDashboard.todoList?.length ? (
            <div className="employee-dashboard-todo-list">
              {filteredDashboard.todoList.map((task) => {
                const isCompleted = task.status === "completed";
                const isTimeOverdue = isTaskTimeOverdue(task);
                const deadlineTime = getTaskDeadlineTime(task);

                return (
                  <label
                    key={task._id}
                    className={`employee-dashboard-todo-item ${isCompleted ? "completed" : ""} ${isTimeOverdue ? "time-overdue" : ""}`}
                  >
                    <input
                      type="checkbox"
                      checked={isCompleted}
                      disabled={updatingTodoId === task._id || isCompleted}
                      onChange={() => handleToggleTodoTask(task)}
                    />
                    <div className="employee-dashboard-todo-copy">
                      <strong>{task.title}</strong>
                      <div className="employee-dashboard-todo-meta">
                        <span className={`employee-status-chip ${isTimeOverdue ? "danger" : isCompleted ? "success" : PRIORITY_META[task.priority]?.className || "info"}`}>
                          {isCompleted ? "Completed" : isTimeOverdue ? "Time Up" : PRIORITY_META[task.priority]?.label || task.priority}
                        </span>
                        <small>
                          {task.dueLabel || formatDeadlineLabel(task.dueDate)}
                          {deadlineTime ? `, ${deadlineTime}` : ""}
                        </small>
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          ) : (
            <EmptyState icon="checkSquare" text="No active tasks matched your current filter." />
          )}

          <button
            type="button"
            className="employee-dashboard-footer-link"
            onClick={() => handleNavigate("tasks")}
          >
            View All Tasks <AppIcon name="chevronRight" />
          </button>
        </article>

        {isTodoModalOpen ? (
          <div className="employee-dashboard-modal-backdrop" role="presentation" onMouseDown={closeTodoModal}>
            <form
              className="employee-dashboard-task-modal"
              onSubmit={handleAddTodoTask}
              onMouseDown={(event) => event.stopPropagation()}
            >
              <div className="employee-dashboard-modal-head">
                <div>
                  <h3>Add Task</h3>
                  <p>Create a personal task with a completion deadline.</p>
                </div>
                <button type="button" aria-label="Close add task" onClick={closeTodoModal}>
                  <AppIcon name="close" />
                </button>
              </div>

              <label className="employee-dashboard-modal-field">
                <span>Task Name</span>
                <input
                  type="text"
                  value={todoForm.title}
                  onChange={(event) => setTodoForm((current) => ({ ...current, title: event.target.value }))}
                  placeholder="Enter task name"
                  autoFocus
                />
              </label>

              <label className="employee-dashboard-modal-field">
                <span>Deadline</span>
                <input
                  type="date"
                  value={todoForm.dueDate}
                  onChange={(event) => setTodoForm((current) => ({ ...current, dueDate: event.target.value }))}
                />
              </label>

              <label className="employee-dashboard-modal-field">
                <span>Deadline Time</span>
                <input
                  type="time"
                  value={todoForm.dueTime}
                  onChange={(event) => setTodoForm((current) => ({ ...current, dueTime: event.target.value }))}
                />
              </label>

              <div className="employee-dashboard-modal-actions">
                <button type="button" onClick={closeTodoModal} disabled={isSavingTodo}>
                  Cancel
                </button>
                <button type="submit" disabled={isSavingTodo}>
                  {isSavingTodo ? "Adding..." : "Add"}
                </button>
              </div>
            </form>
          </div>
        ) : null}

        {isNoteModalOpen ? (
          <div className="employee-dashboard-modal-backdrop" role="presentation" onMouseDown={closeNoteModal}>
            <form
              className="employee-dashboard-task-modal employee-dashboard-note-modal"
              onSubmit={handleAddQuickNote}
              onMouseDown={(event) => event.stopPropagation()}
            >
              <div className="employee-dashboard-modal-head">
                <div>
                  <h3>Add Note</h3>
                  <p>Save a quick reminder or idea for yourself.</p>
                </div>
                <button type="button" aria-label="Close add note" onClick={closeNoteModal}>
                  <AppIcon name="close" />
                </button>
              </div>

              <label className="employee-dashboard-modal-field">
                <span>Headline</span>
                <input
                  type="text"
                  value={noteForm.title}
                  onChange={(event) => setNoteForm((current) => ({ ...current, title: event.target.value }))}
                  placeholder="Enter note headline"
                  autoFocus
                />
              </label>

              <label className="employee-dashboard-modal-field">
                <span>Details</span>
                <textarea
                  value={noteForm.details}
                  onChange={(event) => setNoteForm((current) => ({ ...current, details: event.target.value }))}
                  placeholder="Write note details"
                  rows={5}
                />
              </label>

              <div className="employee-dashboard-modal-actions">
                <button type="button" onClick={closeNoteModal} disabled={isSavingNote}>
                  Cancel
                </button>
                <button type="submit" disabled={isSavingNote}>
                  {isSavingNote ? "Adding..." : "Add Note"}
                </button>
              </div>
            </form>
          </div>
        ) : null}

        <aside className="employee-dashboard-side-stack">
          <article className={`employee-dashboard-card employee-dashboard-attendance-card ${attendanceMeta.className}`}>
            <div className="employee-dashboard-attendance-head">
              <div className={`employee-dashboard-attendance-icon ${attendanceMeta.className}`}>
                <AppIcon name={attendanceMeta.icon} />
              </div>
              <span className={`employee-status-chip ${attendanceMeta.className === "absent" ? "danger" : attendanceMeta.className === "late" ? "warning" : attendanceMeta.className === "leave" ? "info" : "success"}`}>
                {attendanceMeta.label}
              </span>
            </div>

            <h3>Check-in</h3>
            <strong className="employee-dashboard-attendance-time">{attendanceHeadline}</strong>
            <p>{attendanceSubline}</p>

            <button
              type="button"
              className={`employee-dashboard-attendance-button ${
                filteredDashboard.checkInCard?.canCheckOut ? "checkout" : ""
              }`}
              onClick={handleAttendanceAction}
              disabled={
                isAttendanceSaving ||
                (!filteredDashboard.checkInCard?.canCheckIn &&
                  !filteredDashboard.checkInCard?.canCheckOut)
              }
            >
              {isAttendanceSaving
                ? "Saving..."
                : filteredDashboard.checkInCard?.canCheckOut
                  ? "Check-out"
                  : filteredDashboard.checkInCard?.canCheckIn
                    ? "Check-in"
                    : "Marked for Today"}
            </button>
          </article>

          <article className="employee-dashboard-card employee-dashboard-events-card">
            <DashboardCardHeader
              title="Upcoming Events"
              actionLabel="View All"
              onAction={() => handleNavigate("announcements")}
            />

            {filteredDashboard.upcomingEvents?.length ? (
              <div className="employee-dashboard-list-stack">
                {filteredDashboard.upcomingEvents.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    className={`employee-dashboard-side-item ${item.tone || "blue"}`}
                    onClick={() => handleNavigate("announcements")}
                  >
                    <div className="employee-dashboard-side-item-icon">
                      <AppIcon name="calendar" />
                    </div>
                    <div className="employee-dashboard-side-item-copy">
                      <strong>{item.title}</strong>
                      <span>{item.subtitle}</span>
                    </div>
                    <small>{item.meta}</small>
                  </button>
                ))}
              </div>
            ) : (
              <EmptyState icon="calendar" text="No upcoming events matched your search." />
            )}
          </article>

          <article className="employee-dashboard-card employee-dashboard-notes-card">
            <DashboardCardHeader
              title="Quick Notes"
              actionLabel="+ Add Note"
              onAction={openNoteModal}
            />

            {filteredDashboard.quickNotes?.length ? (
              <div className="employee-dashboard-note-list">
                {filteredDashboard.quickNotes.map((note) => (
                  <article
                    key={note.key}
                    className={`employee-dashboard-note-card ${note.tone || "amber"}`}
                  >
                    <button
                      type="button"
                      className="employee-dashboard-note-delete"
                      aria-label={`Delete ${note.title}`}
                      onClick={() => handleDeleteQuickNote(note._id)}
                      disabled={deletingNoteId === note._id}
                    >
                      <AppIcon name="trash" />
                    </button>
                    <strong>{note.title}</strong>
                    <p>{note.description}</p>
                    <span>{note.timeLabel}</span>
                  </article>
                ))}
              </div>
            ) : (
              <EmptyState icon="pin" text="No notes yet. Add your first quick note." />
            )}
          </article>
        </aside>

        <article className="employee-dashboard-card employee-dashboard-my-tasks-card">
          <DashboardCardHeader
            title="My Tasks"
            actionLabel="View All"
            onAction={() => handleNavigate("tasks")}
          />

          <div className="employee-dashboard-task-summary-list">
            {(filteredDashboard.myTasks || []).map((item) => (
              <div key={item.key} className="employee-dashboard-task-summary-item">
                <div className="employee-dashboard-task-summary-head">
                  <div>
                    <strong>{item.label}</strong>
                    <small>{item.percentage}% of tasks</small>
                  </div>
                  <span>{formatNumberValue(item.count)}</span>
                </div>
                <div className="employee-dashboard-progress">
                  <span
                    className={item.tone || "blue"}
                    style={{ width: `${Math.max(6, item.percentage || 0)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </article>

        <article className="employee-dashboard-card employee-dashboard-time-card">
          <DashboardCardHeader
            title="Time Logged (Today)"
            actionLabel="Attendance"
            onAction={() => handleNavigate("attendance")}
          />
          <TimeBreakdownChart breakdown={filteredDashboard.timeBreakdown} />
        </article>

        <article className="employee-dashboard-card employee-dashboard-activity-card">
          <DashboardCardHeader
            title="Activity Feed"
            actionLabel="View All"
            onAction={() => handleNavigate("notifications")}
          />

          {filteredDashboard.activityFeed?.length ? (
            <div className="employee-dashboard-activity-list">
              {filteredDashboard.activityFeed.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  className="employee-dashboard-activity-item"
                  onClick={() => handleNavigate("notifications")}
                >
                  <EmployeeAvatar
                    employee={employee}
                    className="employee-dashboard-activity-avatar"
                    showPresence
                  />

                  <div className="employee-dashboard-activity-copy">
                    <strong>{item.title}</strong>
                    <span>{item.subtitle}</span>
                  </div>

                  <span className={`employee-dashboard-activity-icon ${item.tone || "blue"}`}>
                    <AppIcon name={item.icon} />
                  </span>
                </button>
              ))}
            </div>
          ) : (
            <EmptyState icon="clock" text="No recent activity matched your search." />
          )}
        </article>

        <ScheduleTimeline
          schedule={filteredDashboard.schedule}
          onViewCalendar={() => handleNavigate("attendance")}
        />
      </div>
    </section>
  );
}
